import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon, Search, X, CreditCard, FileText, DollarSign, ScanQrCode, Shield, Eye, Gem, TrendingUp } from 'lucide-react';
import { saleService } from '@/services/sale.service';
import { toast } from 'sonner';
import { ReceiptModal } from '@/components/ReceiptModal';
import { WarrantyModal } from '@/components/WarrantyModal';
import { GarantiaModal } from '@/components/GarantiaModal';
import { useNavigate } from 'react-router-dom';

export interface Sale {
  id: number;
  codepagarme?: string;
  amount: number;
  nome_cliente?: string;
  payment_method: string;
  date_created: string;
  situation?: string;
  installments: number;
  warranty_id: number;
  diamonds?: number;
  userClient: {
    id: number;
    codigo: string;
  };
  warranty?: {
    idwarranty: number;
    url_download?: string;
    data_compra: string;
    client: {
      nome_cliente: string;
      telefone_cliente: string;
      cpf_cliente: string;
    };
    codigos: string;
  };
  revendedora:any;
}

export interface CodigoPeca {
  id: number;
  code: string;
  edit?: boolean;
}

interface SalesTableProps {
  userId: number;
  userRole?: string;
  title?: string;
}

export const SalesTable: React.FC<SalesTableProps> = ({ userId, userRole, title = userRole === 'cliente' ? 'Compras' : 'Vendas' }) => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [filteredSales, setFilteredSales] = useState<Sale[]>([]);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [receiptModalOpen, setReceiptModalOpen] = useState<boolean>(false);
  const [warrantyModalOpen, setWarrantyModalOpen] = useState<boolean>(false);
  const [garantiaModalOpen, setGarantiaModalOpen] = useState<boolean>(false);
  const [saleForGarantia, setSaleForGarantia] = useState<Sale | null>(null);
  const [foundClient, setFoundClient] = useState<boolean>(false);
  const [isSubmittingGarantia, setIsSubmittingGarantia] = useState<boolean>(false);
  const [userData, setUserData] = useState<any>(null);
  const [filtersExpanded, setFiltersExpanded] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;

  const navigate = useNavigate();
  

  const [garantiaFormData, setGarantiaFormData] = useState({
    cpf_cliente: '',
    telefone_cliente: '',
    nome_cliente: '',
    codigos: [] as CodigoPeca[],
    idcliente: null as number | null,
    sale: {} as any,
    iduser: null as number | null,
    sale_date_created: ''
  });

  useEffect(() => {
    loadSales();
    const storedUser = localStorage.getItem('userlogged');
    if (storedUser) {
      setUserData(JSON.parse(storedUser));
    }
  }, [userId]);

  const loadSales = async () => {
    setIsLoading(true);
    try {
      const response = await saleService.getAllSalesByUserId(userId);
      if (response && Array.isArray(response)) {
        setSales(response);
        setFilteredSales(response);
      }
    } catch (error) {
      console.error('Erro ao buscar vendas:', error);
      toast.error('Erro ao buscar vendas');
    } finally {
      setIsLoading(false);
    }
  };

  const mapPaymentMethod = (paymentMethod: string): string => {
    switch (paymentMethod) {
      case 'credit_card':
        return 'Cartão de Crédito';
      case 'pix':
        return 'PIX';
      default:
        return 'Não identificado';
    }
  };

  const getSituationStyles = (situation?: string) => {
    switch (situation?.toLowerCase()) {
      case 'pendente':
        return { badge: 'bg-blue-100 text-blue-800 border-blue-200', text: 'text-blue-600' };
      case 'aprovado':
        return { badge: 'bg-green-100 text-green-800 border-green-200', text: 'text-green-600' };
      case 'reprovado':
        return { badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', text: 'text-yellow-600' };
      case 'baixado':
        return { badge: 'bg-slate-100 text-slate-800 border-slate-200', text: 'text-slate-600' };
      default:
        return { badge: 'bg-gray-100 text-gray-800 border-gray-200', text: 'text-gray-600' };
    }
  };

  const getSituationText = (situation?: string): string => {
    switch (situation?.toLowerCase()) {
      case 'pendente':
        return 'Em Aberto';
      case 'aprovado':
        return 'Aprovado';
      case 'reprovado':
        return 'Reprovado';
      case 'baixado':
        return 'Baixado';
      default:
        return 'N/A';
    }
  };

  const formatCurrency = (amount: number): string => {
    return (amount / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const formatDate = (dateString: string): string => {
    return format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: ptBR });
  };

  const calculateTotals = () => {
    const totals = {
      general: 0,
      diamonds: 0,
      pending: 0,
      approved: 0,
      rejected: 0,
      settled: 0,
    };

    sales.forEach((sale) => {
      totals.general += sale.amount || 0;
      totals.diamonds += sale.diamonds || 0;

      switch (sale.situation?.toLowerCase()) {
        case 'pendente':
          totals.pending += sale.amount || 0;
          break;
        case 'aprovado':
          totals.approved += sale.amount || 0;
          break;
        case 'reprovado':
          totals.rejected += sale.amount || 0;
          break;
        case 'baixado':
          totals.settled += sale.amount || 0;
          break;
      }
    });

    return totals;
  };

  const calculateFilteredTotals = () => {
    const totals = {
      general: 0,
      diamonds: 0,
      pending: 0,
      approved: 0,
      rejected: 0,
      settled: 0,
    };

    filteredSales.forEach((sale) => {
      totals.general += sale.amount || 0;
      totals.diamonds += sale.diamonds || 0;

      switch (sale.situation?.toLowerCase()) {
        case 'pendente':
          totals.pending += sale.amount || 0;
          break;
        case 'aprovado':
          totals.approved += sale.amount || 0;
          break;
        case 'reprovado':
          totals.rejected += sale.amount || 0;
          break;
        case 'baixado':
          totals.settled += sale.amount || 0;
          break;
      }
    });

    return totals;
  };

  const applyFilter = (value: string) => {
    setSearchFilter(value);
    setCurrentPage(1);
    filterSales(value, startDate, endDate);
  };

  const filterSales = (search: string, start?: Date, end?: Date) => {
    let filtered = [...sales];

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(sale =>
        (sale.codepagarme && sale.codepagarme.toLowerCase().includes(searchLower)) ||
        (sale.nome_cliente && sale.nome_cliente.toLowerCase().includes(searchLower)) ||
        mapPaymentMethod(sale.payment_method).toLowerCase().includes(searchLower)
      );
    }

    if (start && end) {
      filtered = filtered.filter(sale => {
        const saleDate = new Date(sale.date_created);
        return saleDate >= start && saleDate <= end;
      });
    }

    setFilteredSales(filtered);
    setCurrentPage(1);
  };

  const aplicarFiltroPorPeriodo = () => {
    if (startDate && endDate) {
      filterSales(searchFilter, startDate, endDate);
    }
  };

  const limparDatas = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    filterSales(searchFilter);
  };

  const downloadReceipt = async () => {
    if (!selectedSale) return;
    
    const element = document.getElementById('receipt-content');
    if (!element) return;

    try {
      const canvas = await (window as any).html2canvas(element, { scale: 2 });
      const img = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = img;
      link.download = `Recibo_${selectedSale.codepagarme || selectedSale.id}.png`;
      link.click();
      toast.success('Recibo baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar recibo:', error);
      toast.error('Erro ao baixar recibo');
    }
  };

  const downloadWarranty = async () => {
    if (!selectedSale) return;
    
    const element = document.getElementById('warranty-content');
    if (!element) return;

    try {
      const canvas = await (window as any).html2canvas(element, { scale: 2 });
      const img = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = img;
      link.download = `Garantia_${selectedSale.codepagarme || selectedSale.id}.png`;
      link.click();
      toast.success('Garantia baixada com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar garantia:', error);
      toast.error('Erro ao baixar garantia');
    }
  };

  const openGarantiaModal = (sale: Sale) => {
    setSaleForGarantia(sale);
    setGarantiaFormData({
      cpf_cliente: '',
      telefone_cliente: '',
      nome_cliente: '',
      codigos: [],
      idcliente: null,
      sale: sale,
      iduser: userData?.id || null,
      sale_date_created: sale.date_created
    });
    setFoundClient(false);
    setGarantiaModalOpen(true);
  };

  const closeGarantiaModal = () => {
    setGarantiaModalOpen(false);
    setSaleForGarantia(null);
    setGarantiaFormData({
      cpf_cliente: '',
      telefone_cliente: '',
      nome_cliente: '',
      codigos: [],
      idcliente: null,
      sale: {},
      iduser: null,
      sale_date_created: ''
    });
    setFoundClient(false);
  };

  const handleCpfChange = (value: string) => {
    setGarantiaFormData(prev => ({
      ...prev,
      cpf_cliente: value
    }));
  };

  const handlePhoneChange = (value: string) => {
    setGarantiaFormData(prev => ({
      ...prev,
      telefone_cliente: value
    }));
  };

  const handleNameChange = (value: string) => {
    setGarantiaFormData(prev => ({
      ...prev,
      nome_cliente: value
    }));
  };

  const submitGarantia = async () => {
    if (!garantiaFormData.nome_cliente || garantiaFormData.codigos.length === 0) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setIsSubmittingGarantia(true);
    try {
      const telefoneClean = garantiaFormData.telefone_cliente.replace(/\D/g, '').trim();

      const garantiaData = {
        ...garantiaFormData,
        telefone_cliente: telefoneClean,
        iduser: userData?.id,
        sale: saleForGarantia || {}
      };

      if (!telefoneClean) {
        toast.error('Telefone inválido, verifique e tente novamente');
        setIsSubmittingGarantia(false);
        return;
      }

      if (!garantiaData.cpf_cliente || garantiaData.cpf_cliente.replace(/\D/g, '').length < 11) {
        toast.error('CPF inválido, verifique e tente novamente');
        setIsSubmittingGarantia(false);
        return;
      }

      const response = await saleService.createGarantia(garantiaData);
      
      if (response && response.success !== false) {
        toast.success('Garantia criada com sucesso!');
        closeGarantiaModal();
        await loadSales();
      } else {
        toast.error(`Erro ao criar garantia. ${response?.message || ''}`);
      }
    } catch (error) {
      console.error('Erro ao criar garantia:', error);
      toast.error('Erro ao criar garantia');
    } finally {
      setIsSubmittingGarantia(false);
    }
  };

  const addCodigo = (codigo: string, tipo: string) => {
    const cleanCodigo = codigo.replace(/\D/g, '');
    
    if (cleanCodigo.length !== 13) {
      toast.error('Cód inválido: fora do padrão de 13 dígitos');
      return;
    }

    const codigoCompleto = `${cleanCodigo} (${tipo})`;
    const exists = garantiaFormData.codigos.some(c => c.code === codigoCompleto);
    
    if (exists) {
      toast.error('Código já adicionado');
      return;
    }

    if (garantiaFormData.codigos.length >= 10) {
      toast.error('Limite de códigos atingidos');
      return;
    }

    const novoCodigo: CodigoPeca = {
      id: Date.now(),
      code: codigoCompleto,
      edit: false
    };

    setGarantiaFormData(prev => ({
      ...prev,
      codigos: [novoCodigo, ...prev.codigos]
    }));
  };

  const removeCodigo = (id: number) => {
    setGarantiaFormData(prev => ({
      ...prev,
      codigos: prev.codigos.filter(c => c.id !== id)
    }));
  };

  if (isLoading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex justify-center items-center h-32">
            <p className="text-gray-500">Carregando {userRole === 'cliente' ? 'compras' : 'vendas'}...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-0 shadow-sm">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {/* Big Numbers - Totais */}
          {(() => {
            const totals = calculateFilteredTotals();
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                {/* Total Geral */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-700">Total Geral</span>
                    <DollarSign className="h-4 w-4 text-blue-500" />
                  </div>
                  <p className="text-2xl font-bold text-blue-900">
                    {formatCurrency(totals.general)}
                  </p>
                </div>

                {/* Diamantes */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-purple-700">Diamantes</span>
                    <Gem className="h-4 w-4 text-purple-500" />
                  </div>
                  <p className="text-2xl font-bold text-purple-900">
                    {totals.diamonds}
                  </p>
                </div>

                {/* Em Aberto */}
                <div className="bg-gradient-to-br from-blue-50 to-cyan-100 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-700">Em Aberto</span>
                    <TrendingUp className="h-4 w-4 text-blue-500" />
                  </div>
                  <p className="text-2xl font-bold text-blue-900">
                    {formatCurrency(totals.pending)}
                  </p>
                </div>

                {/* Aprovado */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-green-700">Aprovado</span>
                    <TrendingUp className="h-4 w-4 text-green-500" />
                  </div>
                  <p className="text-2xl font-bold text-green-900">
                    {formatCurrency(totals.approved)}
                  </p>
                </div>

                {/* Reprovado */}
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-yellow-700">Reprovado</span>
                    <TrendingUp className="h-4 w-4 text-yellow-500" />
                  </div>
                  <p className="text-2xl font-bold text-yellow-900">
                    {formatCurrency(totals.rejected)}
                  </p>
                </div>

                {/* Baixado */}
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">Baixado</span>
                    <TrendingUp className="h-4 w-4 text-slate-500" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {formatCurrency(totals.settled)}
                  </p>
                </div>
              </div>
            );
          })()}
          {/* Filtros */}
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block">Buscar</label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Busque por código, cliente ou forma de pagamento..."
                    value={searchFilter}
                    onChange={(e) => applyFilter(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>
              <div className="pt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                >
                  Filtros
                </Button>
              </div>
            </div>

            {filtersExpanded && (
              <div className="border rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-sm font-medium mb-2 block">Data Início</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !startDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {startDate ? format(startDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={setStartDate}
                          initialFocus
                          locale={ptBR}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="flex-1 min-w-[200px]">
                    <label className="text-sm font-medium mb-2 block">Data Fim</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !endDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate ? format(endDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={setEndDate}
                          initialFocus
                          locale={ptBR}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={aplicarFiltroPorPeriodo} disabled={!startDate || !endDate} size="sm">
                    Aplicar Período
                  </Button>
                  <Button onClick={limparDatas} variant="outline" size="sm">
                    <X className="h-4 w-4 mr-1" />
                    Limpar
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Tabela de Vendas */}
          {filteredSales.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left p-3 font-semibold text-xs text-muted-foreground">Código</th>
                    <th className="text-left p-3 font-semibold text-xs text-muted-foreground">Valor</th>
                    <th className="text-left p-3 font-semibold text-xs text-muted-foreground">{userRole === 'cliente' ? 'Revendedora' : 'Cliente'}</th>
                    <th className="text-left p-3 font-semibold text-xs text-muted-foreground">Pagamento</th>
                    <th className="text-left p-3 font-semibold text-xs text-muted-foreground">Data</th>
                    <th className="text-left p-3 font-semibold text-xs text-muted-foreground">Situação</th>
                    <th className="text-right p-3 font-semibold text-xs text-muted-foreground">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((sale) => {
                    const situationStyles = getSituationStyles(sale.situation);
                    return (
                      <tr key={sale.id} className="border-b hover:bg-muted/40 transition-colors">
                        <td className="p-3 text-xs text-muted-foreground font-mono">
                          #{sale.codepagarme || `XDINF${sale.id}`}
                        </td>
                        <td className="p-3">
                          <span className="font-bold text-green-600">{formatCurrency(sale.amount)}</span>
                          {sale.diamonds ? (
                            <div className="flex items-center gap-1 mt-0.5">
                              <Gem className="h-3 w-3 text-purple-500" />
                              <span className="text-xs font-semibold text-purple-600">{sale.diamonds}</span>
                            </div>
                          ) : null}
                        </td>
                        <td className="p-3 text-sm">
                          {userRole !== 'cliente' ? (
                            <div className="flex flex-col">
                              {sale.userClient?.id ? (
                                <span
                                  className="cursor-pointer text-primary hover:underline font-medium"
                                  onClick={() => window.open(`/v2/usuarios/${sale.userClient.id}`, '_blank')}
                                >
                                  {sale.nome_cliente || 'Não informado'}
                                </span>
                              ) : (
                                <span>{sale.nome_cliente || 'Não informado'}</span>
                              )}
                              {sale.userClient?.id ? (
                                <span className="text-xs text-muted-foreground">#{sale.userClient.codigo}</span>
                              ) : (
                                <span className="text-xs text-muted-foreground italic">S/ Cadastro</span>
                              )}
                            </div>
                          ) : (
                            <span
                              className="cursor-pointer hover:underline"
                              onClick={() => sale.revendedora?.id && window.open(`/v2/usuarios/${sale.revendedora.id}`, '_blank')}
                            >
                              {sale.revendedora?.nome}
                              {sale.revendedora?.codigo && (
                                <span className="text-xs text-muted-foreground ml-1">#{sale.revendedora.codigo}</span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1.5">
                            {sale.payment_method === 'pix' ? (
                              <ScanQrCode className="h-3.5 w-3.5 text-muted-foreground" />
                            ) : (
                              <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                            )}
                            <span className="text-xs">
                              {mapPaymentMethod(sale.payment_method)}
                              {sale.payment_method !== 'pix' && ` (${sale.installments}x)`}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                          {formatDate(sale.date_created)}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className={cn("text-xs", situationStyles.badge)}>
                            {getSituationText(sale.situation)}
                          </Badge>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              title="Ver Recibo"
                              onClick={() => { setSelectedSale(sale); setReceiptModalOpen(true); }}
                            >
                              <FileText className="h-3.5 w-3.5" />
                            </Button>
                            {sale.warranty_id === 0 ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="Gerar Garantia"
                                onClick={() => openGarantiaModal(sale)}
                              >
                                <Shield className="h-3.5 w-3.5" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="Ver Garantia"
                                onClick={() => { setSelectedSale(sale); setWarrantyModalOpen(true); }}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Nenhuma {userRole === 'cliente' ? 'compra' : 'venda'} encontrada</p>
            </div>
          )}

          {filteredSales.length > 0 && (() => {
            const totalPages = Math.ceil(filteredSales.length / PAGE_SIZE);
            return (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-2 mt-4 pt-4 border-t">
                <span className="text-sm text-muted-foreground">
                  Mostrando {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredSales.length)} de {filteredSales.length} {userRole === 'cliente' ? 'compra' : 'venda'}(s)
                </span>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Anterior</Button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                      .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                        if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((item, idx) =>
                        item === 'ellipsis' ? (
                          <span key={`e-${idx}`} className="px-1 text-muted-foreground">…</span>
                        ) : (
                          <Button
                            key={item}
                            variant={currentPage === item ? 'default' : 'outline'}
                            size="sm"
                            className="w-8 h-8 p-0"
                            onClick={() => setCurrentPage(item as number)}
                          >
                            {item}
                          </Button>
                        )
                      )
                    }
                    <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Próximo</Button>
                  </div>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Modais */}
      {selectedSale && (
        <WarrantyModal
          isOpen={warrantyModalOpen}
          onClose={() => setWarrantyModalOpen(false)}
          selectedSale={selectedSale}
          userData={userData}
          onDownloadWarranty={downloadWarranty}
        />
      )}

      {selectedSale && (
        <ReceiptModal
          isOpen={receiptModalOpen}
          onClose={() => setReceiptModalOpen(false)}
          selectedSale={selectedSale}
          userData={userData}
          onDownloadReceipt={downloadReceipt}
        />
      )}

      <GarantiaModal
        isOpen={garantiaModalOpen}
        onClose={closeGarantiaModal}
        saleForGarantia={saleForGarantia}
        garantiaFormData={garantiaFormData}
        foundClient={foundClient}
        isSubmittingGarantia={isSubmittingGarantia}
        onCpfChange={handleCpfChange}
        onPhoneChange={handlePhoneChange}
        onNameChange={handleNameChange}
        onSearchClient={() => {}}
        onAddCodigo={addCodigo}
        onRemoveCodigo={removeCodigo}
        onSubmitGarantia={submitGarantia}
      />
    </>
  );
};

export default SalesTable;
